/** Mutable 3-vector. Methods mutate `this` and return it unless noted. */
export class Vec3 {
  constructor(public x = 0, public y = 0, public z = 0) {}

  static readonly ZERO = Object.freeze(new Vec3(0, 0, 0)) as Vec3;
  static readonly UP = Object.freeze(new Vec3(0, 1, 0)) as Vec3;

  set(x: number, y: number, z: number): this { this.x = x; this.y = y; this.z = z; return this; }
  copy(v: Vec3): this { this.x = v.x; this.y = v.y; this.z = v.z; return this; }
  clone(): Vec3 { return new Vec3(this.x, this.y, this.z); }
  add(v: Vec3): this { this.x += v.x; this.y += v.y; this.z += v.z; return this; }
  sub(v: Vec3): this { this.x -= v.x; this.y -= v.y; this.z -= v.z; return this; }
  mul(v: Vec3): this { this.x *= v.x; this.y *= v.y; this.z *= v.z; return this; }
  scale(s: number): this { this.x *= s; this.y *= s; this.z *= s; return this; }
  addScaled(v: Vec3, s: number): this { this.x += v.x * s; this.y += v.y * s; this.z += v.z * s; return this; }
  negate(): this { this.x = -this.x; this.y = -this.y; this.z = -this.z; return this; }
  dot(v: Vec3): number { return this.x * v.x + this.y * v.y + this.z * v.z; }
  cross(v: Vec3): this {
    const ax = this.x, ay = this.y, az = this.z;
    this.x = ay * v.z - az * v.y;
    this.y = az * v.x - ax * v.z;
    this.z = ax * v.y - ay * v.x;
    return this;
  }
  lengthSq(): number { return this.x * this.x + this.y * this.y + this.z * this.z; }
  length(): number { return Math.sqrt(this.lengthSq()); }
  normalize(): this {
    const l = this.length();
    if (l > 1e-8) { this.x /= l; this.y /= l; this.z /= l; } else { this.x = 0; this.y = 0; this.z = 0; }
    return this;
  }
  setLength(l: number): this { return this.normalize().scale(l); }
  clampLength(max: number): this {
    const l = this.length();
    if (l > max && l > 1e-8) this.scale(max / l);
    return this;
  }
  lerp(v: Vec3, t: number): this {
    this.x += (v.x - this.x) * t; this.y += (v.y - this.y) * t; this.z += (v.z - this.z) * t;
    return this;
  }
  distanceTo(v: Vec3): number { return Math.sqrt(this.distanceToSq(v)); }
  distanceToSq(v: Vec3): number {
    const dx = this.x - v.x, dy = this.y - v.y, dz = this.z - v.z;
    return dx * dx + dy * dy + dz * dz;
  }
  /** Horizontal (XZ) distance. */
  distanceToXZ(v: Vec3): number {
    const dx = this.x - v.x, dz = this.z - v.z;
    return Math.sqrt(dx * dx + dz * dz);
  }
  equals(v: Vec3, eps = 1e-6): boolean {
    return Math.abs(this.x - v.x) < eps && Math.abs(this.y - v.y) < eps && Math.abs(this.z - v.z) < eps;
  }
  min(v: Vec3): this { this.x = Math.min(this.x, v.x); this.y = Math.min(this.y, v.y); this.z = Math.min(this.z, v.z); return this; }
  max(v: Vec3): this { this.x = Math.max(this.x, v.x); this.y = Math.max(this.y, v.y); this.z = Math.max(this.z, v.z); return this; }
  isZero(eps = 1e-8): boolean { return this.lengthSq() < eps * eps; }
  toArray(out: number[] | Float32Array = [], offset = 0): typeof out {
    out[offset] = this.x; out[offset + 1] = this.y; out[offset + 2] = this.z; return out;
  }
  fromArray(a: ArrayLike<number>, offset = 0): this { this.x = a[offset]; this.y = a[offset + 1]; this.z = a[offset + 2]; return this; }
  toString(): string { return '(' + this.x.toFixed(3) + ', ' + this.y.toFixed(3) + ', ' + this.z.toFixed(3) + ')'; }

  static add(a: Vec3, b: Vec3): Vec3 { return new Vec3(a.x + b.x, a.y + b.y, a.z + b.z); }
  static sub(a: Vec3, b: Vec3): Vec3 { return new Vec3(a.x - b.x, a.y - b.y, a.z - b.z); }
  static scale(a: Vec3, s: number): Vec3 { return new Vec3(a.x * s, a.y * s, a.z * s); }
  static cross(a: Vec3, b: Vec3): Vec3 { return a.clone().cross(b); }
  static dot(a: Vec3, b: Vec3): number { return a.dot(b); }
  static lerp(a: Vec3, b: Vec3, t: number): Vec3 { return a.clone().lerp(b, t); }
  static dist(a: Vec3, b: Vec3): number { return a.distanceTo(b); }
}

/** Reusable scratch vectors for hot paths. Never hold a reference across calls. */
export const tmpV = [new Vec3(), new Vec3(), new Vec3(), new Vec3(), new Vec3(), new Vec3()];
